import type { ApolloCache, FetchResult } from '@apollo/client';
import { useMutation } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import ConfirmationModal from '../../components/ConfirmationModal';
import { EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import type { DataKeysForFetchResult } from '../../utils/graphql/typeUtils';
import updateApolloList from '../../utils/graphql/updateApolloList';
import useRemoveHash from '../../utils/router/useRemoveHash';
import commentThreadHash from '../../utils/url/commentThreadHash';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import useShareSecret from '../useShareSecret';

const COMMENT_FRAGMENT = gql(`
  fragment useCommentActions_comment on PublicCommentFields {
    id
    reactions {
      id
      emoji
      author {
        id
      }
    }
  }
`);

const POST_FRAGMENT = gql(`
  fragment useCommentActions_post on RootPostFields {
    id
    ...postPath_post
  }
`);

const DELETE_COMMENT_MUTATION = gql(`
  mutation deleteComment($id: ID!) {
    deleteComment(commentId: $id) {
      id
    }
  }
`);

const UPDATE_COMMENT_MUTATION = gql(`
  mutation updateComment($id: ID!, $content: String!) {
    updateComment(commentId: $id, content: $content) {
      id
      content
      updatedAt
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const CREATE_COMMENT_REACTION_MUTATION = gql(`
  mutation createCommentReaction($commentId: ID!, $emoji: String!) {
    commentReaction: createCommentReaction(
      commentId: $commentId
      emoji: $emoji
    ) {
      ...CommentReactionList_reactions
    }
  }
`);

const UPDATE_COMMENT_REACTION_QUERY = gql(`
  query updateCommentReaction($postId: ID!) {
    post(id: $postId) {
      id
      threads {
        id
        comments {
          id
          reactions {
            id
          }
        }
      }
    }
  }
`);

const updateCommentReactionList = <TData>(
  { threadId, commentId }: { threadId: string; commentId: string },
  prev: DocumentType<typeof UPDATE_COMMENT_REACTION_QUERY>,
  data: TData,
  itemKey: DataKeysForFetchResult<TData>,
  add = true,
) => ({
  ...prev,
  post: prev.post && {
    ...prev.post,
    threads: prev.post.threads?.map((thread) =>
      thread.id !== threadId
        ? thread
        : {
            ...thread,
            comments: thread.comments?.map((comment) =>
              comment.id !== commentId
                ? comment
                : updateApolloList(comment, data, 'reactions', itemKey, add),
            ),
          },
    ),
  },
});

function updateCommentReactionQueryList<TData>(
  {
    postId,
    threadId,
    commentId,
  }: { postId: string; threadId: string; commentId: string },
  dataKey: DataKeysForFetchResult<TData>,
  add = true,
) {
  return (cache: ApolloCache<unknown>, { data }: FetchResult<TData>) => {
    try {
      const query = UPDATE_COMMENT_REACTION_QUERY;
      const prev = cache.readQuery({ query, variables: { postId } });
      if (prev && data) {
        const updated = updateCommentReactionList(
          { threadId, commentId },
          prev,
          data,
          dataKey,
          add,
        );
        cache.writeQuery({ query, variables: { postId }, data: updated });
      }
    } catch (e) {
      // readQuery failed
    }
  };
}

const DELETE_COMMENT_REACTION_MUTATION = gql(`
  mutation deleteCommentReaction($id: ID!) {
    commentReaction: deleteCommentReaction(id: $id) {
      ...CommentReactionList_reactions
    }
  }
`);

const RESOLVE_THREAD_MUTATION = gql(`
  mutation resolveThread($id: ID!) {
    resolveThread(threadId: $id) {
      id
    }
  }
`);

const DELETE_THREAD_MUTATION = gql(`
  mutation deleteThread($id: ID!) {
    deleteThread(threadId: $id) {
      id
    }
  }
`);

const useCommentActions = (
  threadId: string,
  post: DocumentType<typeof POST_FRAGMENT>,
  comment?: DocumentType<typeof COMMENT_FRAGMENT>,
) => {
  const shareSecret = useShareSecret();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const removeHash = useRemoveHash();

  const [deleteCommentMutation] = useMutation(DELETE_COMMENT_MUTATION, {
    update: (proxy, { data }) => {
      if (data?.deleteComment) {
        proxy.modify({
          id: proxy.identify({ __typename: 'Thread', id: threadId }),
          fields: {
            comments: (existingComments, { readField }) =>
              existingComments.filter(
                (existingComment: { id: string }) =>
                  readField('id', existingComment) !== data.deleteComment.id,
              ),
          },
        });
      }
    },
  });
  const [updateCommentMutation] = useMutation(UPDATE_COMMENT_MUTATION);

  const [createCommentReactionMutation] = useMutation(
    CREATE_COMMENT_REACTION_MUTATION,
  );
  const [deleteCommentReactionMutation] = useMutation(
    DELETE_COMMENT_REACTION_MUTATION,
  );

  const [resolveThreadMutation] = useMutation(RESOLVE_THREAD_MUTATION);
  const [deleteThreadMutation] = useMutation(DELETE_THREAD_MUTATION);

  const deleteComment = () =>
    comment && deleteCommentMutation({ variables: { id: comment.id } });

  const updateComment = (content: string) =>
    comment &&
    updateCommentMutation({ variables: { id: comment.id, content } });

  const createCommentReaction = (emoji: string) =>
    comment &&
    createCommentReactionMutation({
      variables: { commentId: comment.id, emoji },
      update: updateCommentReactionQueryList(
        { threadId, postId: post.id, commentId: comment.id },
        'commentReaction',
        true,
      ),
    });

  const deleteCommentReaction = (id: string) =>
    comment &&
    deleteCommentReactionMutation({
      variables: { id },
      update: updateCommentReactionQueryList(
        { threadId, postId: post.id, commentId: comment.id },
        'commentReaction',
        false,
      ),
    });

  const focusCommentThread = () => {
    dispatch(
      EditorActions.focusComment({
        id: threadId,
        isActive: true,
        hasExpandIntent: true,
      }),
    );
  };

  const toggleCommentReaction = (emoji: string) => {
    if (!comment) {
      return;
    }
    const reaction = comment.reactions.find(
      (item) => item.emoji === emoji && item.author.id === currentUser.id,
    );

    focusCommentThread();

    if (reaction) {
      deleteCommentReaction(reaction.id);
    } else {
      dispatch(UIActions.addToRecentlyUsedEmojis(emoji));
      createCommentReaction(emoji);
    }
  };

  const resolveThread = async () => {
    await resolveThreadMutation({ variables: { id: threadId } });

    if (window.location.hash === commentThreadHash(threadId)) {
      removeHash();
    }
  };

  const deleteThread = () => {
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Delete entire thread?',
        message: 'All comments in the thread will be deleted.',
        confirmButtonText: 'Delete',
        onConfirm: () => {
          if (window.location.hash === commentThreadHash(threadId)) {
            removeHash();
          }
          return deleteThreadMutation({ variables: { id: threadId } });
        },
      }),
    );
  };

  const copyThreadLink = () => {
    const url = fullOrgUrl(
      postPath(post, { shareSecret, anchor: commentThreadHash(threadId) }),
    );

    navigator.clipboard.writeText(url).then(() => {
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Link copied to clipboard',
        }),
      );
    });
  };

  return {
    deleteComment,
    updateComment,
    toggleCommentReaction,
    resolveThread,
    deleteThread,
    copyThreadLink,
    focusCommentThread,
  };
};
export default useCommentActions;
